import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { RosterGroupService } from '../instances/roster-group';
import { RosterGroupConverter } from '@/converters';
import { RosterReorderRequest } from '../request/roster-group';

export const useRosterGroup = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ROSTER_GROUP', params],
    async () => {
      const [err, data] = await RosterGroupService.getRosterGroups(params);
      if (err) {
        throw err;
      }
      return {
        data: RosterGroupConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useRosterGroupById = (id: string) => {
  const query = useQuery(
    ['GET_ROSTER_GROUP_BY_ID', id],
    async () => {
      const [err, data] = await RosterGroupService.getRosterGroupById(id);
      if (err) {
        throw err;
      }
      return RosterGroupConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetRosterGroupById = () => {
  return useMutation({
    mutationKey: ['GET_ROSTER_GROUP_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await RosterGroupService.getRosterGroupById(uuid);
      if (err) {
        throw err;
      }
      return RosterGroupConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostRosterGroupMutation = () => {
  return useMutation({
    mutationKey: ['POST_ROSTER_GROUP'],
    mutationFn: async (payload: any) => {
      const [err, data] = await RosterGroupService.postRosterGroup(payload);
      if (err) {
        throw err;
      }
      return RosterGroupConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutRosterGroupMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ROSTER_GROUP'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await RosterGroupService.putRosterGroup(id, payload);
      if (err) {
        throw err;
      }
      return RosterGroupConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteRosterGroupMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_ROSTER_GROUP'],
    mutationFn: async (id: string) => {
      const [err] = await RosterGroupService.deleteRosterGroup(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useReorderRosterGroupsMutation = () => {
  return useMutation({
    mutationKey: ['REORDER_ROSTER_GROUPS'],
    mutationFn: async (payload: RosterReorderRequest) => {
      const [err] = await RosterGroupService.reorderRosterGroups(payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
